import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  useGridApiContext,
  GridRenderHeaderFilterProps,
  GridLoadingOverlay,
} from "@mui/x-data-grid-pro";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Button,
  Box,
  IconButton,
  CircularProgress,
  TextField,
  Checkbox,
  Autocomplete,
  debounce,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import { UserRoleMapModel, FilterModel } from "../../../all_Interfaces";
import { UserRoleMapService } from "../../../services/UserRoleMapService";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import userRoleMapColumns from "./userRoleMapColumns.json";
import { log } from "util";

interface CustomTextHeaderFilterProps {
  item: any;
  onFilterChange: (field: string, value: string) => void;
}

const UserRoleMapDashboard: React.FC = () => {
  const navigate = useNavigate();
  const mastersService = new UserRoleMapService();

  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState<UserRoleMapModel[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [valueDb, setValueDb] = useState<any>({});
  const [dbFilters, setDbFilters] = useState({ role: [], status: [] });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const [filterModel, setFilterModel] = useState<FilterModel>({
    first: 0,
    rows: 10,
    sortOrder: 1,
    filters: [],
    globalFilter: null,
    sortField: "",
  });

  const [pageSize, setPageSize] = useState<number>(25);

  useEffect(() => {
    getUserRoleData(filterModel);
    setLoading(false);
  }, []);

  const getUserRoleData = async (filterParams: FilterModel) => {
    setLoading(true);
    try {
      const response = await mastersService.FetchUserRoleDashboardData(
        filterParams
      );
      setRowData(response.userRoleMaps);
      setTotalRecords(response.rowCount);

      setDbFilters({
        ...dbFilters,
        role: response.roles,
        status: response.status,
      });
    } catch (error) {
      console.error("Error fetching user role data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    const updatedFilter = {
      ...filterModel,
      first: newPaginationModel.page * newPaginationModel.pageSize,
      rows: newPaginationModel.pageSize,
    };
    setFilterModel(updatedFilter);
    getUserRoleData(updatedFilter);
  };

  const handleSortChange = (sortModel: GridSortModel) => {
    const updatedFilter = {
      ...filterModel,
      sortField: sortModel[0]?.field || "",
      sortOrder: sortModel[0]?.sort === "asc" ? 1 : -1,
    };
    setFilterModel(updatedFilter);
    getUserRoleData(updatedFilter);
  };

  const viewUserRoleMap = (userRole: any, mode: string) => {
    if (mode === "view") {
      navigate(`/config/user/view/${userRole.itemId}`);
    } else if (mode === "edit") {
      navigate(`/config/user/edit/${userRole.itemId}`);
    }
  };

  const getValues = (data: any[]): string[] => {
    try {
      return (
        data?.map((item) => {
          if (!item.value) {
            throw new Error(`Missing value in item: ${JSON.stringify(item)}`);
          }
          return item.value;
        }) || []
      );
    } catch (error) {
      console.error("Error extracting values:", error);
      return [];
    }
  };

  const CustomDropDownHeaderFilter = (params: any) => {
    const apiRef = useGridApiContext();
    const { colDef } = params;
    const field = colDef?.field || params.field;
    const fieldData = dbFilters[field] || [];
    const columnFilterList = fieldData.map((element: any) => element.value);
    const [localValue, setLocalValue] = useState<string[]>(
      valueDb[field] || []
    );

    useEffect(() => {
      setLocalValue(valueDb[field] || []);
    }, [valueDb[field]]);

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Autocomplete
          multiple
          limitTags={1}
          fullWidth
          size="small"
          onFocus={() => apiRef.current.startHeaderFilterEditMode(colDef.field)}
          onChange={(event, newValue) => {
            setLocalValue(newValue);
            handleDropdownFilterChange(field, newValue);
          }}
          value={localValue}
          options={columnFilterList}
          disableCloseOnSelect
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
                checked={selected}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={localValue.length === 0 ? "All" : ""}
              variant="standard"
              size="small"
              sx={{
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "14px",
                  textAlign: "center",
                  opacity: 0.7,
                },
                "& .MuiInputBase-root": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          )}
        />
      </Box>
    );
  };

  const CustomTextHeaderFilter: React.FC<CustomTextHeaderFilterProps> =
    React.memo(({ item, onFilterChange }) => {
      const apiRef = useGridApiContext();
      const [localValue, setLocalValue] = useState(valueDb[item.field] || "");

      useEffect(() => {
        setLocalValue(valueDb[item.field] || "");
      }, [valueDb[item.field]]);

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
            paddingBottom: "10px",
          }}
        >
          <TextField
            id="standard-basic"
            variant="standard"
            size="small"
            value={localValue}
            onFocus={() => apiRef.current.startHeaderFilterEditMode(item.field)}
            onChange={(event) => {
              setLocalValue(event.target.value);
              onFilterChange(item.field, event.target.value);
            }}
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        </Box>
      );
    });

  const actionColumns = {
    field: "actions",
    headerName: "Actions",
    headerAlign: "center",
    flex: 0.5,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Box>
        <IconButton
          onClick={() => viewUserRoleMap(params.row, "view")}
          sx={{ color: "black" }}
        >
          <VisibilityIcon sx={{ fontSize: 16, color: "#263746" }} />
        </IconButton>
        <IconButton
          onClick={() => viewUserRoleMap(params.row, "edit")}
          sx={{ color: "black" }}
        >
          <EditIcon sx={{ fontSize: 16, color: "#263746" }} />
        </IconButton>
      </Box>
    ),
  };

  const sendRequest = () => {
    getUserRoleData(filterModel);
  };

  const ref = useRef(sendRequest);

  useEffect(() => {
    ref.current = sendRequest;
  }, [filterModel]);

  const debouncedCallback = useMemo(() => {
    const func = () => {
      ref.current();
    };
    return debounce(func, 1000);
  }, []);

  const handleDropdownFilterChange = (field: string, values: any) => {
    setValueDb((prev) => ({ ...prev, [field]: values }));
    if (values.length === 0) {
      setFilterModel((prev) => ({
        ...prev,
        filters: [...prev.filters.filter((f) => f.identity !== field)],
        first: 0,
        rows: pageSize,
      }));
    } else {
      setFilterModel((prev) => ({
        ...prev,
        filters: [
          ...prev.filters.filter((f) => f.identity !== field),
          { identity: field, matchMode: "in", value: values },
        ],
        first: 0,
        rows: pageSize,
      }));
    }

    debouncedCallback();
  };

  const handleTextfieldFilterChange = useCallback(
    (field: string, value: string) => {
      setValueDb((prev) => ({ ...prev, [field]: value }));

      if (value === "") {
        setFilterModel((prev) => ({
          ...prev,
          filters: prev.filters.filter((f) => f.identity !== field),
          first: 0,
          rows: pageSize,
        }));
      } else {
        setFilterModel((prev) => ({
          ...prev,
          filters: [
            ...prev.filters.filter((f) => f.identity !== field),
            { identity: field, matchMode: "contains", value: [value] },
          ],
          first: 0,
          rows: pageSize,
        }));
      }

      debouncedCallback();
    },
    [pageSize]
  );

  const columns = useMemo(
    () =>
      [actionColumns, ...userRoleMapColumns.coldDef0].map((colDef: any) => {
        let { valueFormatter, ...rest } = colDef;

        if (colDef === actionColumns) {
          return rest;
        }
        if (["role", "status"].includes(colDef.field)) {
          return {
            ...rest,
            renderHeaderFilter: (params: any) => (
              <CustomDropDownHeaderFilter
                {...params}
                dbFilters={dbFilters}
                onFilterChange={handleDropdownFilterChange}
                getValues={getValues}
                dbValues={valueDb}
              />
            ),
          };
        }

        return {
          ...rest,
          renderHeaderFilter: (params: any) => (
            <CustomTextHeaderFilter
              {...params}
              onFilterChange={handleTextfieldFilterChange}
              getValues={getValues}
            />
          ),
        };
      }),
    [dbFilters, handleTextfieldFilterChange]
  );

  return (
    <Box
      sx={{
        width: "calc(100% - 32px)",
        margin: 2,
        boxSizing: "border-box",
        paddingBottom: { xs: "50px", sm: "100px", md: "150px" },
      }}
    >
      <Box sx={{ textAlign: "right", mb: 2,marginRight:1}}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          className="new-lease-btn"
          onClick={() => navigate("/config/user/new")}
        >
          New User
        </Button>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <DataGridPro
          disableColumnFilter
          columns={columns}
          rows={rowData || []}
          loading={loading}
          pagination
          paginationMode="server"
          className="dashboard-dataGrid"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          onSortModelChange={handleSortChange}
          pageSizeOptions={[10, 25, 50]}
          columnHeaderHeight={48}
          headerFilterHeight={60}
          rowCount={totalRecords}
          headerFilters
          getRowId={(row) => row.itemId}
          slots={{
            loadingOverlay: GridLoadingOverlay,
          }}
          sx={{
            width: "100%",
            height: "100%",
            pointerEvents: loading ? "none" : "auto",
            opacity: loading ? 0.7 : 1,
            fontSize: "12px",
            // Add these styles for alternating rows
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f5f5f5", // Light grey for odd rows
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#ffffff", // White for even rows
            },
            // Optional hover effect
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#e8f4f8", // Light blue on hover
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default UserRoleMapDashboard;
